/// <reference path="custom-typing.d.ts" />
import * as Highcharts from 'highcharts/highstock';
import addNoData from 'highcharts/modules/no-data-to-display';
/**
 * Custom Highcharts SVG Symbols
 *
 * These functions return a list of SVG path commands with their parameters.
 * The list is used to render a shape relative to the (0, 0) xy coordinate of each data point's symbol.
 * To use one of these custom symbols, set the shape attribute to the symbol's name. i.e. { ... shape: 'ycCross' }
 *
 * Note: The ratios below were calculated based on the smallest version of each shape shown on: https://ycharts.netlify.app/scatterplot.html
 *
 * @param x - The x coordinate on the chart, that represents where x=0 for the symbol on the chart. x=0 is left.
 * @param y - The y coordinate on the chart, that represents where y=0 for the symbol on the chart. y=0 is top.
 * @param w - The width of the symbol, which is equal to plotOptions.scatter.marker.radius.
 * @param h - The height of the symbol, which is equal to plotOptions.scatter.marker.radius.
 *
 * Overview of SVG commands:
 * Resources:
 * Detailed explanation of SVG path commands: https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d
 * Interactive Bezier curve demo: https://www.sitepoint.com/html5-svg-cubic-curves/
 * Highcharts marker symbol docs: https://api.highcharts.com/highcharts/plotOptions.series.marker.symbol
 *
 * M (x, y)+ - Move the current point to the x,y coordinate. We use this to start each path.
 * z - Connect the last point of the path with its initial point. We use this to end each path.
 * l (dx, dy)+ - Draw a line from the current point to the end point.
 *      end point = (x + dx, y + dy)
 * h (dx)+ - Draw a horizontal line from the current point to the end point.
 *      end point = (x + dx, y)
 *      i.e. h10 moves right ten pixels. h-10 moves left ten pixels.
 * v (dy)+ - Draw a vertical line from the current point to the end point.
 *      end point = (x, y + dy)
 *      i.e. v10 moves down ten pixels. v-10 moves up ten pixels.
 * c (dx1, dy1, dx2, dy2, dx, dy)+ - Draw a cubic Bézier curve from the current point to the end point.
 *      where the line begins to curve = (x + dx1, y + dy1). A straight line is drawn from start point to this point.
 *      where the line stops curving = (x + dx2, y + dy2). A straight line is drawn from this point to end point.
 *      end point = (x + dx, y + dy)
 *      i.e. c(5, 0, 5, -5, 0, -5) draws an arc that is concave left.
 *      The line begins curving on the right and stops curving above.
 * */
/**
 * A cross shape with curved tips, drawn clockwise, starting from the top curve.
 * Width of each arm = w * 2 / 11
 * Height of each arm (excluding the curving tip) = h * 3.5 / 11
 * */
Highcharts.SVGRenderer.prototype.symbols.ycCross = function (x, y, w, h) {
    /* eslint-disable */
    return [
        // Top arm - starting with the curving tip
        'M', x + (w * 4.5 / 11), y + (h / 11),
        'c', 0, -(h / 11), (w * 2 / 11), -(h / 11), (w * 2 / 11), 0,
        'v', (h * 3.5 / 11),
        // Right arm
        'h', (w * 3.5 / 11),
        'c', (w / 11), 0, (w / 11), (h * 2 / 11), 0, (h * 2 / 11),
        'h', -(w * 3.5 / 11),
        // Bottom arm
        'v', (h * 3.5 / 11),
        'c', 0, (h / 11), -(w * 2 / 11), (h / 11), -(w * 2 / 11), 0,
        'v', -(h * 3.5 / 11),
        // Left arm
        'h', -(w * 3.5 / 11),
        'c', -(w / 11), 0, -(w / 11), -(h * 2 / 11), 0, -(h * 2 / 11),
        'h', (w * 3.5 / 11),
        // Top arm - ending with the left side
        'v', -(w * 3.5 / 11),
        // Close the path
        'z'
    ];
    /* eslint-enable */
};
/**
 * A diamond shape drawn clockwise, starting from the top.
 * */
Highcharts.SVGRenderer.prototype.symbols.ycDiamond = function (x, y, w, h) {
    /* eslint-disable */
    return [
        'M', x + (w / 2), y,
        'l', (w * 5 / 13), (h / 2), // downward right diagonal
        -(w * 5 / 13), (h / 2), // downward left diagonal
        -(w * 5 / 13), -(h / 2), // upward left diagonal
        'z' // close path, automatically draws upward right diagonal
    ];
    /* eslint-enable */
};
/**
 * A triangle with one tip pointing up, drawn clockwise, starting from the top.
 * */
Highcharts.SVGRenderer.prototype.symbols.ycTriangle = function (x, y, w, h) {
    /* eslint-disable */
    return [
        'M', x + (w / 2), y,
        'l', (w / 2), h, // downward right diagonal
        -w, 0, // horizontal left
        'z' // close path, automatically draws upward right diagonal
    ];
    /* eslint-enable */
};
/**
 * Check whether the user zoomed in or reset zoom (meaning, they zoomed back out to default).
 */
function checkZoom() {
    const chart = this;
    const { userMax, userMin } = chart.xAxis[0].getExtremes();
    if (!chart.custom || !('zoomed' in chart.custom))
        return;
    // userMax and userMin are defined only if the user makes a selection. A selection must be made to zoom.
    // Resetting the zoom is a button click not a selection, so userMax and userMin will be undefined.
    chart.custom = {
        ...chart.custom,
        zoomed: !!(userMax && userMin),
    };
}
/**
 * Reposition the chart, such that the y axis is not cut off and
 * reposition the zoom controls to appear within the chart's top right corner.
 */
export function positionChartElements(chart) {
    const chartControls = document.querySelector('.scatter-chart-controls');
    const plotContainer = chart.container.querySelector('.highcharts-plot-background');
    const spacing = chart.options.chart?.spacing;
    if (chartControls && plotContainer && spacing) {
        // Extra space (10px) is added to prevent the chart from overflowing its container
        // eslint-disable-next-line no-param-reassign
        chart.container.style.marginTop = `-${spacing[0] + 10}px`;
        chartControls.style.right = '24px';
    }
}
// Add custom events
Highcharts.addEvent(Highcharts.Chart, 'render', checkZoom);
// By default, if you select a zoom area that is above the data max, it will use the data max as the zoom area max,
// Which relegates the peak of the chart data to the top of the visible chart area, making it difficult to see and annotate that peak.
// Setting allowZoomOutside = true allows you to zoom such that the data min or max in the chart is centered in the visible chart area after zooming.
// I haven't found a well-documented way to achieve this. This is sort of a hack based on reading the Highcharts source code:
// https://github.com/highcharts/highcharts/blob/v11.1.0/ts/Core/Axis/Axis.ts#L2695
// @ts-ignore
Highcharts.Axis.prototype.allowZoomOutside = true;
// Add modules to Highcharts
// Show no data message when there is no data
addNoData(Highcharts);
/** Export the 'central' Highcharts import.
 *  Note: All chart code for the scatterPlotHighchartsApp should use this import instead of importing from 'highcharts'
 *  so that Highcharts is properly configured for scatter plots. */
export default Highcharts;
